import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import InputField from "../../components/ConsultationTranscribedPrescriptions/InputField";
import RadioGroup from "../../components/ConsultationTranscribedPrescriptions/RadioGroup";
import CheckboxGroup from "../../components/ConsultationTranscribedPrescriptions/CheckboxGroup";
import BackBtn from "../../assets/images/backicon.jpg";
import "./index.css";
import { post, get } from "../../services/ApiService.js";
import { useParams } from "react-router-dom";

const IndividualTravelConsultation = () => {
  const { refNo } = useParams();
  const [formData, setFormData] = useState({
    answers: [],
    explanations: [],
    userInfo: {
      name: "",
      email: "",
      phone: "",
      dob: ""
    }
  });
  const [form, setForm] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0); // 0 = splash, 1+ = questions
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [apiError, setApiError] = useState(null); // For API error messages

  useEffect(() => {
    fetchData();
  }, [refNo]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await get(`api/travel-clinic/consultation/${refNo}`);
      console.log("API Response:", res);
      setIsLoading(false);
      if (res.success) {
        setForm(res.form);
        setQuestions(res.form.questions);
        if (res.patient) {
          setFormData(prev => ({
            ...prev,
            userInfo: {
              name: res.patient.name || "",
              email: res.patient.email || "",
              phone: res.patient.phone || "",
              dob: res.patient.dob || ""
            }
          }));
        }
        setApiError(null);
      } else {
        setApiError("No Assessment found for you");
      }
    } catch (error) {
      setIsLoading(false);
      setApiError("An error occurred while loading the assessment");
      console.log(error);
    }
  };

  const getCurrentQuestion = () => {
    return questions.find(q => q.step === currentStep); // Adjust for splash screen
  };

  const handleAnswer = (questionId, optionId, text) => {
    const question = questions.find(q => q._id === questionId);
    if (!question) return;

    const currentIndex = questions.findIndex(q => q._id === questionId);

    setErrors(prev => {
      const updated = { ...prev };
      delete updated[currentIndex];
      return updated;
    });

    const newAnswers = [...formData.answers];
    const currentAnswer = newAnswers[currentIndex] || {
      _id: question._id,
      options: [],
      description: ""
    };

    // Handle answers based on input type
    if (question.inputType === "radio" || question.inputType === "select") {
      currentAnswer.options = [optionId];
    } else if (question.inputType === "checkbox") {
      const currentOptions = currentAnswer.options || [];
      const isExclusive = question.options.find(
        opt => opt._id === optionId
      )?.exclusive;

      if (isExclusive) {
        currentAnswer.options = [optionId];
      } else {
        const filteredOptions = currentOptions.filter(
          opt => !question.options.find(o => o._id === opt)?.exclusive
        );

        if (currentOptions.includes(optionId)) {
          currentAnswer.options = filteredOptions.filter(opt => opt !== optionId);
        } else {
          currentAnswer.options = [...filteredOptions, optionId];
        }
      }
    } else if (question.inputType === "text") {
      currentAnswer.description = text;
    }

    newAnswers[currentIndex] = currentAnswer;

    // Handle explanations
    if (question.inputType !== "text") {
      const requiresExplanation = currentAnswer.options?.some(
        optId => question.options.find(o => o._id === optId)?.requireExplanation
      );

      setFormData(prev => {
        const newExplanations = [...prev.explanations];

        if (requiresExplanation) {
          const existingIndex = newExplanations.findIndex(ex => ex._id === question._id);

          if (existingIndex === -1) {
            newExplanations.push({ _id: question._id, text: "" });
          }
        } else {
          return {
            ...prev,
            answers: newAnswers,
            explanations: newExplanations.filter(ex => ex._id !== question._id)
          };
        }

        return {
          ...prev,
          answers: newAnswers,
          explanations: newExplanations
        };
      });
    } else {
      setFormData(prev => ({
        ...prev,
        answers: newAnswers
      }));
    }
  };

  const handleExplanationChange = (questionId, text) => {
    setFormData(prev => {
      const newExplanations = [...prev.explanations];
      const existingIndex = newExplanations.findIndex(ex => ex._id === questionId);

      if (existingIndex >= 0) {
        newExplanations[existingIndex] = { ...newExplanations[existingIndex], text };
      } else {
        newExplanations.push({ _id: questionId, text });
      }

      return {
        ...prev,
        explanations: newExplanations
      };
    });
  };

  const isCurrentQuestionValid = () => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) return false;

    const currentIndex = questions.findIndex(q => q._id === currentQuestion._id);
    const currentAnswer = formData.answers[currentIndex] || { options: [], description: "" };

    // Check if question is answered
    let isAnswered = false;
    if (currentQuestion.inputType === "text") {
      isAnswered = currentAnswer.description?.trim() !== "";
    } else {
      isAnswered = currentAnswer.options?.length > 0;
    }

    if (!isAnswered) return false;

    // Check if explanation is required and provided
    const requiresExplanation = currentQuestion.options?.some(
      opt => currentAnswer.options?.includes(opt._id) && opt.requireExplanation
    );

    if (requiresExplanation) {
      const explanation = formData.explanations.find(ex => ex._id === currentQuestion._id);
      if (!explanation || !explanation.text?.trim()) return false;
    }

    return true;
  };

  const handleNext = () => {
    if (isCurrentQuestionValid() && currentStep < questions.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isCurrentQuestionValid()) {
      const payload = {
        answers: formData.answers,
        explanations: formData.explanations,
        formId: form._id,
        userInfo: formData.userInfo,
      };
      try {
        setIsSubmit(true);
        const res = await post("api/travel-clinic/consultation", payload);
        setIsSubmit(false);
        if (res.success) {
          setIsSubmitted(true);
        } else {
          setApiError(res.message || "Submission failed. Please try again.");
        }
      } catch (error) {
        setIsSubmit(false);
        setApiError("An error occurred while submitting. Please try again.");
        console.log(error);
      }
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (apiError && currentStep !== 0) {
    return (
      <div className="transcribed-consultation-form">
        <div className="py-7 px-4 mb-0 mt-6 text-center header-form-wrap">
          <img src="/logo.png" width={150} />
        </div>
        <div className="mt-0 container">
          <div className="row justify-content-md-center g-3">
            <div className="col-12">
              <div className="w-form fade-in">
                <div className="row g-5">
                  <div className="col-12 text-center">
                    <h3>Error</h3>
                    <div className="mt-5 p-4">
                      <p className="text-danger">No Assessment found for you</p>
                      <button
                        className="btncust activebg mt-4"
                        onClick={() => window.location.reload()}
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Splash Screen
  if (currentStep === 0) {
    return (
      <div className="transcribed-consultation-form">
        <div className="py-7 px-4 mb-0 mt-6 text-center header-form-wrap">
          <img src="/logo.png" width={150} />
        </div>

        <div className="mt-0 container">
          <div className="row justify-content-md-center g-3">
            <div className="col-12">
              <div className="w-form fade-in">
                <div className="row g-5">
                  <div className="col-12 text-center">
                    <h3>Travel Vaccine Risk Assessment</h3>
                    <div className="mt-5 p-4">
                      <h4>Before You Begin</h4>
                      <p className="mt-3">
                        This assessment will help us determine which travel vaccines are appropriate for you based on your
                        health history and travel plans. Please answer all questions.
                      </p>
                      <p className="mt-3">
                        The assessment will take approximately 5 minutes to complete.
                      </p>
                      <button
                        className="btncust activebg mt-4"
                        onClick={() => setCurrentStep(1)} // Start directly with questions
                      >
                        Get Started
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="transcribed-consultation-form">
        <div className="py-7 px-4 mb-0 mt-6 text-center header-form-wrap">
          <img src="/logo.png" width={150} />
        </div>

        <div className="mt-0 container">
          <div className="row justify-content-md-center g-3">
            <div className="col-12">
              <div className="w-form fade-in">
                <div className="row g-5">
                  <div className="col-12 text-center">
                    <h3>Thank You!</h3>
                    <div className="mt-5 p-4">
                      <h4>Your assessment has been submitted</h4>
                      <p className="mt-3">
                        Our team will review your answers and get back to you shortly.
                      </p>
                      <p className="mt-3">
                        If you have any questions, please contact us at:
                      </p>
                      <ul className="list-unstyled">
                        <li>Email: <a href="mailto:info@myprivatechemist.com">info@myprivatechemist.com</a></li>
                        <li>Phone: <a href="tel:02039879202">020 3987 9202</a></li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Assessment Questions
  const currentQuestion = getCurrentQuestion();
  const currentIndex = questions.findIndex(q => q._id === currentQuestion._id);
  const currentAnswer = formData.answers[currentIndex] || { options: [], description: "" };
  const requiresExplanation = currentQuestion.options?.some(
    opt => currentAnswer.options?.includes(opt._id) && opt.requireExplanation
  );
  const currentExplanation = formData.explanations.find(ex => ex._id === currentQuestion._id) || { text: "" };

  return (
    <div className="transcribed-consultation-form">
      <div className="py-7 px-4 mb-0 mt-6 text-center header-form-wrap">
        <img src="/logo.png" width={150} />
      </div>

      <div className="mt-0 container">
        <div className="row justify-content-md-center g-3">
          <div className="col-12">
            <div className="w-form fade-in">
              <div className="row g-5">
                <div className="col-12">
                  <h3>Travel vaccine risk assessment</h3>
                </div>
                <div className="col-lg-12">
                  {apiError && (
                    <div className="alert alert-danger">
                      {apiError}
                    </div>
                  )}

                  <Form.Group controlId={`formStep${currentQuestion.step}`}>
                    <h5 className="mb-5">{currentQuestion.title}</h5>
                    {currentQuestion.explanationText && (
                      <p className="mb-4">{currentQuestion.explanationText}</p>
                    )}

                    {currentQuestion.inputType === "text" && (
                      <InputField
                        label=""
                        name={currentQuestion._id}
                        value={currentAnswer.description || ""}
                        onChange={(e) => handleAnswer(currentQuestion._id, null, e.target.value)}
                      />
                    )}

                    {currentQuestion.inputType === "radio" && (
                      <RadioGroup
                        name={currentQuestion._id}
                        options={currentQuestion.options.map(opt => ({
                          label: opt.optionText,
                          value: opt._id
                        }))}
                        selectedValue={currentAnswer.options[0] || ""}
                        onChange={(value) => handleAnswer(currentQuestion._id, value, "")}
                      />
                    )}

                    {currentQuestion.inputType === "checkbox" && (
                      <CheckboxGroup
                        label=""
                        name={currentQuestion._id}
                        options={currentQuestion.options.map(opt => ({
                          label: opt.optionText,
                          value: opt._id
                        }))}
                        selectedValues={currentAnswer.options || []}
                        onChange={(value) => handleAnswer(currentQuestion._id, value, "")}
                      />
                    )}

                    {requiresExplanation && (
                      <div className="mb-10 mt-4">
                        <label className="form-label">Please specify</label>
                        <textarea
                          className="input_text metropill"
                          rows="3"
                          value={currentExplanation.text}
                          placeholder="Type here..."
                          onChange={(e) => handleExplanationChange(currentQuestion._id, e.target.value)}
                        ></textarea>
                      </div>
                    )}
                  </Form.Group>

                  <div className="d-flex justify-content-between mt-4">
                    <div className="card-footer p-0 d-flex align-items-center gap-2 bg-transparent border-0">
                      <img
                        src={BackBtn}
                        onClick={handlePrevious}
                        disabled={currentStep === 1} // First question is step 1 now
                        style={{ cursor: currentStep === 1 ? 'not-allowed' : 'pointer' }}
                        width="35px"
                        className="cursor-pointer"
                        alt="Back"
                      />
                    </div>

                    {currentStep < questions.length ? (
                      <div className="card-footer p-0 d-flex align-items-center gap-2 bg-transparent border-0 w-auto">
                        <button
                          type="button"
                          className={`btncust w-100 ${isCurrentQuestionValid() ? 'activebg' : 'btn-disable'}`}
                          onClick={handleNext}
                          disabled={!isCurrentQuestionValid()}
                          style={{ cursor: isCurrentQuestionValid() ? 'pointer' : 'not-allowed' }}
                        >
                          Next
                        </button>
                      </div>
                    ) : (
                      <div className="card-footer p-0 d-flex align-items-center gap-2 bg-transparent border-0 w-100">
                        <button
                          type="button"
                          className={`btncust w-auto ${isCurrentQuestionValid() ? 'activebg' : 'btn-disable'}`}
                          onClick={handleSubmit}
                          disabled={!isCurrentQuestionValid() || isSubmit}
                          style={{ cursor: isCurrentQuestionValid() ? 'pointer' : 'not-allowed' }}
                        >
                          {isSubmit ? 'Please wait...' : 'Submit'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { IndividualTravelConsultation };